/**
 * KeywordTooltip.tsx — inline keyword reference. Uses keyword-glossary.json
 * (built from SKILL.md §7 by scripts/build-keyword-glossary.mjs).
 *
 * KeywordSpan: wraps a word — if the word matches a known keyword
 * (case-insensitive), renders an underlined trigger; on hover/click,
 * shows a tooltip with the definition + skill citation.
 */

import { useState } from 'react';
import glossaryData from '../../data/keyword-glossary.json' with { type: 'json' };

type GlossaryEntry = {
  keyword: string;
  definition: string;
  citation: string;
};

const GLOSSARY: ReadonlyMap<string, GlossaryEntry> = new Map(
  (glossaryData as readonly GlossaryEntry[]).map((entry) => [entry.keyword.toLowerCase(), entry]),
);

type Props = {
  word: string;
  /** What to render when no glossary match (defaults to the word itself). */
  fallback?: string;
};

export function KeywordSpan({ word, fallback }: Props) {
  // Strip punctuation when looking up, but render with original casing.
  const normalised = word.replace(/[^a-zA-Z]/g, '').toLowerCase();
  const entry = GLOSSARY.get(normalised);

  const [hovered, setHovered] = useState(false);

  if (!entry) {
    return <>{fallback ?? word}</>;
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: tooltip trigger is purely informational; keyboard users can read the inline text without activation.
    <span
      className="relative cursor-help underline decoration-dotted decoration-amber-300 underline-offset-2"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {fallback ?? word}
      {hovered && (
        <span className="absolute bottom-full left-0 mb-1 z-30 w-64 p-2 bg-cosmos-900 ring-1 ring-amber-300 rounded text-xs text-cosmos-100 pointer-events-none shadow-2xl">
          <span className="block font-bold text-amber-300 mb-0.5">
            {entry.keyword} <span className="text-cosmos-400 font-normal">({entry.citation})</span>
          </span>
          <span className="block text-cosmos-200">{entry.definition}</span>
        </span>
      )}
    </span>
  );
}
